import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, takeUntil, filter, finalize, take } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ReservationsDataSource } from 'src/app/DataSources/reservations.data-source';
import { AlertInfo, AlertService, AlertType } from 'src/app/Services/Alert/alert.service';
import {
  BulkCancelReservationsResult,
  ReservationService,
} from 'src/app/Services/Reservation/reservation.service';
import { CurrentUserService } from 'src/app/Services/CurrentUser/current-user.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { RoleConst } from 'src/app/Models/roles.const';
import { TableActionGroup, TableColumn, TableIcon } from '../table/table.component';

@Component({
  standalone: false,
  selector: 'app-reservations',
  templateUrl: './reservations.component.html',
  styleUrls: ['./reservations.component.scss'],
  providers: [DatePipe],
})
export class ReservationsComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly reservationSyncStorageKey = 'ewrs.reservations.sync';
  private readonly reservationSyncSourceId = `${Date.now()}-${Math.random()}`;

  readonly myBookingsTableColumns: TableColumn<any>[] = [
    {
      id: 'bookedBy',
      header: 'Type',
      showHeader: false,
      type: 'icons',
      align: 'center',
      columnWidth: 60,
      icons: (row) => this.getBookedByIcons(row),
    },
    {
      id: 'date',
      header: 'Date',
      showHeader: true,
      type: 'text',
      columnWidth: 130,
      text: (row) => this.formatDate(row?.date),
    },
    {
      id: 'deskNumber',
      header: 'Desk No.',
      showHeader: true,
      type: 'text',
      columnWidth: 150,
      text: (row) => row?.workstation?.number ?? '',
    },
    {
      id: 'location',
      header: 'Location',
      showHeader: true,
      type: 'text',
      columnWidth: 176,
      text: (row) => row?.workstation?.floor?.building?.name ?? '',
    },
    {
      id: 'floor',
      header: 'Floor No.',
      showHeader: true,
      type: 'text',
      columnWidth: 150,
      text: (row) => row?.workstation?.floor?.name ?? '',
    },
    {
      id: 'time',
      header: 'Selected time',
      showHeader: true,
      type: 'text',
      columnWidth: 144,
      text: (row) => row?.reservationTimeSlot?.name ?? '',
    },
    {
      id: 'actions',
      header: 'Actions',
      showHeader: false,
      type: 'actionGroup',
      align: 'right',
      columnWidth: 238,
      actions: (row) => this.getMyBookingActions(row),
    },
  ];

  dataSource: ReservationsDataSource;
  readonly searchBookingsTableColumns: TableColumn<any>[] = [
    {
      id: 'name',
      header: 'Email',
      showHeader: true,
      type: 'text',
      columnWidth: 240,
      text: (row) => row?.user?.emailAddress ?? '',
    },
    {
      id: 'date',
      header: 'Date',
      showHeader: true,
      type: 'text',
      columnWidth: 120,
      text: (row) => this.formatDate(row?.date),
    },
    {
      id: 'deskNumber',
      header: 'Desk No.',
      showHeader: true,
      type: 'text',
      columnWidth: 140,
      text: (row) => row?.workstation?.number ?? '',
    },
    {
      id: 'location',
      header: 'Location',
      showHeader: true,
      type: 'text',
      columnWidth: 180,
      text: (row) => row?.workstation?.floor?.building?.name ?? '',
    },
    {
      id: 'floor',
      header: 'Floor No.',
      showHeader: true,
      type: 'text',
      columnWidth: 130,
      text: (row) => row?.workstation?.floor?.name ?? '',
    },
    {
      id: 'time',
      header: 'Selected time',
      showHeader: true,
      type: 'text',
      columnWidth: 150,
      text: (row) => row?.reservationTimeSlot?.name ?? '',
    },
    {
      id: 'actions',
      header: 'Actions',
      showHeader: false,
      type: 'actionGroup',
      align: 'right',
      columnWidth: 420,
      actions: (row) => this.getSearchBookingActions(row),
    },
  ];
  dataSourceAll: ReservationsDataSource;

  userId: number;
  emailAddress: string;
  private me: any;

  isSuperUser = false;
  curUser: any;
  private readonly destroy$ = new Subject<void>();

  myBookingsData: any[] = [];
  private searchBookingsData: any[] = [];
  selectedReservationIds: ReadonlySet<number> = new Set<number>();
  isBulkCancelling: boolean = false;
  readonly canSelectMyBooking = (reservation: any): boolean => !!reservation?.canCancel;

  myBookingsPageIndex: number = 0;
  myBookingsPageSize: number = 10;
  readonly myBookingsPageSizeOptions: number[] = [10, 20, 50, 100];

  searchBookingsPageIndex: number = 0;
  searchBookingsPageSize: number = 100;
  readonly searchBookingsPageSizeOptions: number[] = [10, 20, 50, 100];

  // @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
  //     this.paginatorAll = mp;
  //     this.setDataSourceAttributes();
  // }

  reservationSearchControl: UntypedFormControl = new UntypedFormControl();
  reservationFilter: string = null;

  constructor(
    private reservationService: ReservationService,
    private currentUserService: CurrentUserService,
    private alertService: AlertService,
    private route: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.onReservationSyncEvent);
    }

    this.applyUser(this.currentUserService.currentUser);
    this.currentUserService.user$
      .pipe(
        filter((user) => !!user),
        take(1)
      )
      .subscribe((user) => this.applyUser(user));

    this.dataSource = new ReservationsDataSource(this.reservationService);
    this.dataSourceAll = new ReservationsDataSource(this.reservationService);

    (this.dataSource as any).subject.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.myBookingsData = Array.from(data || []);
      this.reconcileSelectedBookings();
    });

    (this.dataSourceAll as any).subject.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.searchBookingsData = Array.from(data || []);
    });

    this.reservationSearchControl.valueChanges
      .pipe(debounceTime(750), takeUntil(this.destroy$))
      .subscribe((value: string) => {
        this.reservationFilter = value.trim();
        this.searchBookingsPageIndex = 0;

        if (value === '') {
          this.getAllReservations('!@#$%^&*()');
          return;
        }

        this.getAllReservationsByFilter();
      });
  }

  /** One click check-in route URL is like this: /reservations?reservationIds=456,789 */
  private checkInFromQueryParams(): void {
    this.route.queryParamMap.pipe(take(1)).subscribe((params) => {
      const reservationIdsParam: string = params.get('reservationIds');
      const reservationIds: number[] = (reservationIdsParam ?? '')
        .split(',')
        .map((id) => Number(id.trim()))
        .filter((id) => Number.isFinite(id) && id > 0);

      // if there's invalid params or no params
      if (reservationIds.length === 0) {
        if (reservationIdsParam) {
          this.clearCheckInQueryParams();
        }
        return;
      }

      this.reservationService
        .getMyReservations(0, 1000)
        .pipe(take(1))
        .subscribe((resp: any) => {
          const reservations: any[] = resp?.value?.result ?? [];
          let reservationToCheckIn: any | null = null;
          let alertToSet: { alertInfo: AlertInfo | null; date: Date | null } = {
            alertInfo: null,
            date: null,
          };

          for (const reservationId of reservationIds) {
            const reservation: any = reservations.find((r: any) => r?.id === reservationId);
            const checkInValidationAlert: AlertInfo | null = this.validateCheckIn(
              reservation,
              true,
              true
            );

            if (checkInValidationAlert) {
              const bookingDate: Date = new Date(reservation?.date);
              const hasValidBookingDate: boolean = !Number.isNaN(bookingDate.getTime());

              if (
                !alertToSet.alertInfo ||
                (hasValidBookingDate &&
                  (!alertToSet.date || bookingDate.getTime() < alertToSet.date.getTime()))
              ) {
                alertToSet.date = hasValidBookingDate ? bookingDate : null;
                alertToSet.alertInfo = checkInValidationAlert;
              }

              continue;
            }

            reservationToCheckIn = reservation;
            break;
          }

          if (reservationToCheckIn == null && alertToSet.alertInfo != null) {
            this.alertService.setAlert(alertToSet.alertInfo);
          } else {
            this.checkIn(reservationToCheckIn.id, reservationToCheckIn, true);
          }

          this.clearCheckInQueryParams();
        });
    });
  }

  private clearCheckInQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }, 50);

    this.checkInFromQueryParams();
    this.getReservations();

    this.getAllReservationsByFilter();
  }

  authorizeSuperUser(roles: any[]): boolean {
    for (let r of roles) {
      if (r.roleTypeId === RoleConst.SuperUser) {
        return true;
      }
    }
    return false;
  }

  private applyUser(user: any | null): void {
    if (!user) {
      return;
    }
    this.isSuperUser = this.authorizeSuperUser(user.userRoles ?? []);
    this.curUser = user;
    this.me = { id: user?.id, firstName: user?.firstName, lastName: user?.lastName };
    this.userId = this.me.id;
    this.emailAddress = user?.emailAddress;
  }

  getReservations() {
    this.dataSource.loadReservations(this.myBookingsPageIndex, this.myBookingsPageSize);
  }

  onMyBookingsPageChanged(event: PageEvent): void {
    this.clearSelectedBookings();
    this.myBookingsPageIndex = event.pageIndex;
    this.myBookingsPageSize = event.pageSize;
    this.getReservations();
  }

  getAllReservations(searchString: string) {
    this.dataSourceAll.loadAllReservations(
      this.searchBookingsPageIndex,
      this.searchBookingsPageSize,
      searchString
    );
  }

  getAllReservationsByFilter() {
    this.dataSourceAll.loadAllReservations(
      this.searchBookingsPageIndex,
      this.searchBookingsPageSize,
      this.reservationFilter
    );
  }

  onSearchBookingsPageChanged(event: PageEvent): void {
    this.searchBookingsPageIndex = event.pageIndex;
    this.searchBookingsPageSize = event.pageSize;
    this.getAllReservationsByFilter();
  }

  checkIn(reservationId: number, reservation?: any, allowInputFallback: boolean = false): void {
    const checkInValidationAlert: AlertInfo | null = this.validateCheckIn(
      reservation,
      false,
      allowInputFallback
    );

    if (checkInValidationAlert) {
      this.alertService.setAlert(checkInValidationAlert);
      return;
    }

    this.reservationService.checkIn(reservationId).subscribe(() => {
      this.applyReservationCheckedInToValidationCaches(reservationId);
      this.publishReservationSyncEvent('checked-in', reservationId);
      this.getReservations();
      this.getAllReservationsByFilter();
      this.alertService.setAlert(<AlertInfo>{
        title: 'Booking checked in',
        message: this.buildReservationAlertMessage('successfully checked in', reservation),
        type: AlertType.Success,
        closeable: true,
      });
    });
  }

  checkOut(reservationId: number, reservation?: any): void {
    const checkOutValidationAlert: AlertInfo | null = this.validateCheckOut(reservation);

    if (checkOutValidationAlert) {
      this.alertService.setAlert(checkOutValidationAlert);
      return;
    }

    this.reservationService.checkOut(reservationId).subscribe(() => {
      this.removeReservationFromValidationCaches(reservationId);
      this.publishReservationSyncEvent('checked-out', reservationId);
      this.getReservations();
      this.getAllReservationsByFilter();
      this.alertService.setAlert(<AlertInfo>{
        title: 'Booking checked out',
        message: this.buildReservationAlertMessage('successfully checked out', reservation),
        type: AlertType.Success,
        closeable: true,
      });
    });
  }

  // Send the confirmation email again
  resend(reservationId: number): void {
    this.reservationService.resend(reservationId).subscribe(() => {
      this.getReservations();
      this.getAllReservationsByFilter();
      this.alertService.setAlert(<AlertInfo>{
        title: 'Booking email sent',
        message: '',
        type: AlertType.Success,
        closeable: true,
      });
    });
  }

  cancel(reservationId: number, reservation?: any): void {
    const cancelValidationAlert: AlertInfo | null = this.validateCancel(reservation);

    if (cancelValidationAlert) {
      this.alertService.setAlert(cancelValidationAlert);
      return;
    }

    this.reservationService.cancelReservation(reservationId).subscribe(() => {
      this.removeReservationFromValidationCaches(reservationId);
      this.publishReservationSyncEvent('cancelled', reservationId);
      this.getReservations();
      this.getAllReservationsByFilter();
      this.alertService.setAlert(<AlertInfo>{
        title: 'Booking cancelled',
        message: this.buildReservationAlertMessage('successfully cancelled', reservation),
        type: AlertType.Success,
        closeable: true,
      });
    });
  }

  cancelClicked(reservationId: number, reservation?: any): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Cancel booking?',
        message: 'Are you sure you want to cancel the desk?',
        cancelText: 'No',
        confirmText: 'Yes',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.cancel(reservationId, reservation);
      }
    });
  }

  onMyBookingSelectionChanged(payload: { row: any; selected: boolean }): void {
    if (!this.canSelectMyBooking(payload.row) || this.isBulkCancelling) {
      return;
    }

    const reservationId = Number(payload.row?.id);
    if (!Number.isFinite(reservationId) || reservationId <= 0) {
      return;
    }

    const selectedIds = new Set(this.selectedReservationIds);
    if (payload.selected) {
      selectedIds.add(reservationId);
    } else {
      selectedIds.delete(reservationId);
    }
    this.selectedReservationIds = selectedIds;
  }

  onVisibleMyBookingSelectionChanged(payload: { rows: any[]; selected: boolean }): void {
    if (this.isBulkCancelling) {
      return;
    }

    const selectedIds = new Set(this.selectedReservationIds);
    for (const reservation of payload.rows) {
      if (!this.canSelectMyBooking(reservation)) {
        continue;
      }

      const reservationId = Number(reservation?.id);
      if (!Number.isFinite(reservationId) || reservationId <= 0) {
        continue;
      }

      if (payload.selected) {
        selectedIds.add(reservationId);
      } else {
        selectedIds.delete(reservationId);
      }
    }
    this.selectedReservationIds = selectedIds;
  }

  clearSelectedBookings(): void {
    if (!this.isBulkCancelling) {
      this.selectedReservationIds = new Set<number>();
    }
  }

  cancelSelectedBookingsClicked(): void {
    const selectedCount = this.selectedReservationIds.size;
    if (selectedCount === 0 || this.isBulkCancelling) {
      return;
    }

    const bookingNoun = selectedCount === 1 ? 'booking' : 'bookings';
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: `Cancel ${selectedCount} ${bookingNoun}?`,
        message: `Are you sure you want to cancel the selected ${selectedCount} ${bookingNoun}? This action cannot be undone.`,
        cancelText: 'Go back',
        confirmText: 'Confirm cancellation',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.cancelSelectedBookings();
        }
      });
  }

  private cancelSelectedBookings(): void {
    const requestedReservationIds = Array.from(this.selectedReservationIds);
    if (requestedReservationIds.length === 0 || this.isBulkCancelling) {
      return;
    }

    this.isBulkCancelling = true;
    this.reservationService
      .cancelReservations(requestedReservationIds)
      .pipe(
        take(1),
        finalize(() => {
          this.isBulkCancelling = false;
        })
      )
      .subscribe({
        next: (response) => {
          const result: BulkCancelReservationsResult = response?.value ?? {
            cancelledReservationIds: [],
            failedReservations: [],
            notificationFailedReservationIds: [],
          };
          const requestedIdSet = new Set(requestedReservationIds);
          const cancelledIds = Array.from(
            new Set(
              (result.cancelledReservationIds ?? [])
                .map((id) => Number(id))
                .filter((id) => requestedIdSet.has(id))
            )
          );
          const cancelledIdSet = new Set(cancelledIds);
          const reportedFailedIdSet = new Set(
            (result.failedReservations ?? [])
              .map((failure) => Number(failure.reservationId))
              .filter((id) => requestedIdSet.has(id) && !cancelledIdSet.has(id))
          );
          const failedIds = requestedReservationIds.filter(
            (id) => reportedFailedIdSet.has(id) || !cancelledIdSet.has(id)
          );
          const notificationFailedIds = Array.from(
            new Set(
              (result.notificationFailedReservationIds ?? [])
                .map((id) => Number(id))
                .filter((id) => cancelledIdSet.has(id))
            )
          );

          for (const reservationId of cancelledIds) {
            this.removeReservationFromValidationCaches(reservationId);
            this.publishReservationSyncEvent('cancelled', reservationId);
          }

          const remainingBookingCount = Math.max(
            0,
            this.dataSource.dataCount - cancelledIds.length
          );
          const lastPageIndex = Math.max(
            0,
            Math.ceil(remainingBookingCount / this.myBookingsPageSize) - 1
          );
          this.myBookingsPageIndex = Math.min(this.myBookingsPageIndex, lastPageIndex);
          this.dataSource.removeReservations(cancelledIdSet);
          this.dataSourceAll.removeReservations(cancelledIdSet);
          this.selectedReservationIds = new Set(failedIds);
          this.getReservations();
          this.getAllReservationsByFilter();
          this.showBulkCancellationResult(
            requestedReservationIds.length,
            cancelledIds,
            failedIds,
            notificationFailedIds
          );
        },
        error: () => {
          // BaseService displays the API error. Keep the current selection so the user can retry.
        },
      });
  }

  private showBulkCancellationResult(
    requestedCount: number,
    cancelledIds: number[],
    failedIds: number[],
    notificationFailedIds: number[]
  ): void {
    const cancelledCount = cancelledIds.length;
    const failedCount = failedIds.length;

    if (failedCount > 0) {
      const cancelledSummary =
        cancelledCount === 1
          ? '1 booking was cancelled.'
          : `${cancelledCount} bookings were cancelled.`;
      const failureSummary =
        failedCount === 1
          ? '1 booking could not be cancelled.'
          : `${failedCount} bookings could not be cancelled.`;

      this.alertService.setAlert(<AlertInfo>{
        title: 'Some bookings could not be cancelled',
        message: `${cancelledSummary} ${failureSummary} Booking IDs: ${failedIds.join(', ')}.`,
        type: AlertType.Error,
        closeable: true,
      });
      return;
    }

    const notificationFailureCount = notificationFailedIds.length;
    if (notificationFailureCount > 0) {
      const notificationNoun = notificationFailureCount === 1 ? 'booking' : 'bookings';
      this.alertService.setAlert(<AlertInfo>{
        title: 'Bookings cancelled with notification issues',
        message: `${cancelledCount} of ${requestedCount} selected bookings were cancelled. Confirmation notifications could not be sent for ${notificationFailureCount} ${notificationNoun}.`,
        type: AlertType.Warning,
        closeable: true,
      });
      return;
    }

    const bookingNoun = cancelledCount === 1 ? 'booking' : 'bookings';
    const verb = cancelledCount === 1 ? 'has' : 'have';
    this.alertService.setAlert(<AlertInfo>{
      title: cancelledCount === 1 ? 'Booking cancelled' : 'Bookings cancelled',
      message: `The selected ${cancelledCount} ${bookingNoun} ${verb} been successfully cancelled.`,
      type: AlertType.Success,
      closeable: true,
    });
  }

  checkOutClicked(reservationId: number, reservation?: any): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Check out?',
        message: 'Are you sure you would like to check out of this booking?',
        cancelText: 'No',
        confirmText: 'Yes',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.checkOut(reservationId, reservation);
      }
    });
  }

  onMyBookingsActionButtonClick(payload: { row: any; columnId: string; actionId?: string }): void {
    if (payload.columnId !== 'actions' || this.isBulkCancelling) {
      return;
    }

    switch (payload.actionId) {
      case 'check-out':
        this.checkOutClicked(payload.row.id, payload.row);
        break;
      case 'check-in':
        this.checkIn(payload.row.id, payload.row);
        break;
      case 'cancel':
      default:
        this.cancelClicked(payload.row.id, payload.row);
        break;
    }
  }

  onSearchBookingsActionButtonClick(payload: {
    row: any;
    columnId: string;
    actionId?: string;
  }): void {
    if (payload.columnId !== 'actions') {
      return;
    }

    switch (payload.actionId) {
      case 'check-out':
        this.checkOutClicked(payload.row.id, payload.row);
        break;
      case 'check-in':
        this.checkIn(payload.row.id, payload.row);
        break;
      case 'resend-email':
        this.resend(payload.row.id);
        break;
      case 'cancel':
      default:
        this.cancelClicked(payload.row.id, payload.row);
        break;
    }
  }

  private formatDate(date: Date | string | undefined): string {
    return this.datePipe.transform(date, 'yyyy-MM-dd') ?? '';
  }

  private normalizeReservationId(reservationId: any): number | string | null {
    if (reservationId == null) {
      return null;
    }

    const numericId: number = Number(reservationId);
    if (Number.isFinite(numericId) && numericId > 0) {
      return numericId;
    }

    const stringId: string = String(reservationId).trim();
    return stringId.length > 0 ? stringId : null;
  }

  private hasMatchingReservationId(
    reservation: any,
    reservationId: number | string | null
  ): boolean {
    if (reservationId == null) {
      return false;
    }

    const currentId: number | string | null = this.normalizeReservationId(reservation?.id);
    return currentId != null && currentId === reservationId;
  }

  private getReservationForValidation(
    reservation: any,
    allowInputFallback: boolean = false
  ): any | null {
    const reservationId: number | string | null = this.normalizeReservationId(reservation?.id);
    if (reservationId == null) {
      return null;
    }

    const dataSourceReservation: any | undefined = this.myBookingsData.find((r) =>
      this.hasMatchingReservationId(r, reservationId)
    );
    if (dataSourceReservation) {
      return dataSourceReservation;
    }

    const dataSourceAllReservation: any | undefined = this.searchBookingsData.find((r) =>
      this.hasMatchingReservationId(r, reservationId)
    );
    if (dataSourceAllReservation) {
      return dataSourceAllReservation;
    }

    if (allowInputFallback) {
      return reservation;
    }

    return null;
  }

  private publishReservationSyncEvent(
    type: 'cancelled' | 'checked-out' | 'checked-in',
    reservationId: number
  ): void {
    if (!reservationId || typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    const payload = {
      type,
      reservationId,
      sourceId: this.reservationSyncSourceId,
    };

    window.localStorage.setItem(this.reservationSyncStorageKey, JSON.stringify(payload));
  }

  private readonly onReservationSyncEvent = (event: StorageEvent): void => {
    if (event.key !== this.reservationSyncStorageKey || !event.newValue) {
      return;
    }

    try {
      const payload: any = JSON.parse(event.newValue);
      const isSupportedEventType: boolean =
        payload?.type === 'cancelled' ||
        payload?.type === 'checked-out' ||
        payload?.type === 'checked-in';

      if (
        payload?.sourceId === this.reservationSyncSourceId ||
        !isSupportedEventType ||
        !payload?.reservationId
      ) {
        return;
      }

      if (payload?.type === 'checked-in') {
        this.applyReservationCheckedInToValidationCaches(Number(payload.reservationId));
        return;
      }

      this.removeReservationFromValidationCaches(Number(payload.reservationId));
    } catch {
      // Malformed cross-tab sync payloads are ignored; caches stay as-is.
    }
  };

  private removeReservationFromValidationCaches(reservationId: number): void {
    const normalizedReservationId: number | string | null =
      this.normalizeReservationId(reservationId);
    if (normalizedReservationId == null) {
      return;
    }

    this.myBookingsData = this.myBookingsData.filter(
      (r) => !this.hasMatchingReservationId(r, normalizedReservationId)
    );
    this.searchBookingsData = this.searchBookingsData.filter(
      (r) => !this.hasMatchingReservationId(r, normalizedReservationId)
    );

    const numericReservationId = Number(normalizedReservationId);
    if (Number.isFinite(numericReservationId)) {
      const selectedIds = new Set(this.selectedReservationIds);
      selectedIds.delete(numericReservationId);
      this.selectedReservationIds = selectedIds;
    }
  }

  private reconcileSelectedBookings(): void {
    if (this.selectedReservationIds.size === 0) {
      return;
    }

    const selectableVisibleIds = new Set(
      this.myBookingsData
        .filter((reservation) => this.canSelectMyBooking(reservation))
        .map((reservation) => Number(reservation?.id))
        .filter((reservationId) => Number.isFinite(reservationId) && reservationId > 0)
    );
    const reconciledIds = new Set(
      Array.from(this.selectedReservationIds).filter((reservationId) =>
        selectableVisibleIds.has(reservationId)
      )
    );

    if (
      reconciledIds.size !== this.selectedReservationIds.size ||
      Array.from(reconciledIds).some(
        (reservationId) => !this.selectedReservationIds.has(reservationId)
      )
    ) {
      this.selectedReservationIds = reconciledIds;
    }
  }

  private applyReservationCheckedInToValidationCaches(reservationId: number): void {
    const normalizedReservationId: number | string | null =
      this.normalizeReservationId(reservationId);
    if (normalizedReservationId == null) {
      return;
    }

    this.myBookingsData = this.myBookingsData.map((reservation) =>
      this.hasMatchingReservationId(reservation, normalizedReservationId)
        ? {
            ...reservation,
            canCheckIn: false,
            canCheckOut: true,
            canCancel: false,
          }
        : reservation
    );

    this.searchBookingsData = this.searchBookingsData.map((reservation) =>
      this.hasMatchingReservationId(reservation, normalizedReservationId)
        ? {
            ...reservation,
            canCheckIn: false,
            canCheckOut: true,
            canCancel: false,
          }
        : reservation
    );

    this.reconcileSelectedBookings();
  }

  private validateCheckIn(
    reservation: any,
    enforceOwnerCheck: boolean = false,
    allowInputFallback: boolean = false
  ): AlertInfo | null {
    reservation = this.getReservationForValidation(reservation, allowInputFallback);

    // if reservation is not found in the user's current reservations
    if (!reservation) {
      return <AlertInfo>{
        title: 'Error checking in',
        message:
          'This booking is no longer active. It may have already been cancelled, removed, or checked out.',
        type: AlertType.Error,
        closeable: true,
      };
    }

    const deskNumber: string = reservation.workstation?.number ?? reservation.workstationNumber;
    const bookingDate: Date = new Date(reservation.date);
    const date: string = this.formatDate(bookingDate ?? new Date());
    const timePeriod: string = reservation.reservationTimeSlot?.name;

    // if reservation has already been checked in by the user
    if (reservation?.canCheckOut) {
      return <AlertInfo>{
        title: 'Already checked in',
        message: `You have already checked in to Desk ${deskNumber} for ${date} (${timePeriod}).`,
        type: AlertType.Warning,
        closeable: true,
      };
    }

    // if reservation is not today or user tries to check-in before 6:00 AM
    if (!reservation?.canCheckIn) {
      return <AlertInfo>{
        title: 'Error checking in',
        message: `You can only check in on the day of your booking. Desk ${deskNumber} is booked for ${date} (${timePeriod}).`,
        type: AlertType.Error,
        closeable: true,
      };
    }

    if (enforceOwnerCheck) {
      const matchesUser: boolean =
        reservation?.user?.id === this.userId ||
        reservation?.user?.emailAddress?.toLowerCase() === this.emailAddress?.toLowerCase();

      // if reservation does not belong to the user
      if (!matchesUser) {
        return <AlertInfo>{
          title: 'Error checking in',
          message: 'You can only check in to your own booking.',
          type: AlertType.Error,
          closeable: true,
        };
      }
    }

    return null;
  }

  private validateCheckOut(reservation: any): AlertInfo | null {
    reservation = this.getReservationForValidation(reservation, false);

    // if reservation is not found in the user's current reservations
    if (!reservation) {
      return <AlertInfo>{
        title: 'Error checking out',
        message:
          'This booking is no longer active. It may have already been cancelled, removed, or checked out.',
        type: AlertType.Error,
        closeable: true,
      };
    }

    const deskNumber: string = reservation.workstation?.number ?? reservation.workstationNumber;
    const bookingDate: Date = new Date(reservation.date);
    const date: string = this.formatDate(bookingDate ?? new Date());
    const timePeriod: string = reservation.reservationTimeSlot?.name;

    // if reservation is not currently checked in by the user
    if (!reservation?.canCheckOut) {
      return <AlertInfo>{
        title: 'Error checking out',
        message: `You have not yet checked in to Desk ${deskNumber} for ${date} (${timePeriod}).`,
        type: AlertType.Error,
        closeable: true,
      };
    }

    return null;
  }

  private validateCancel(reservation: any): AlertInfo | null {
    reservation = this.getReservationForValidation(reservation, false);

    // if reservation is not found in the user's current reservations
    if (!reservation) {
      return <AlertInfo>{
        title: 'Error cancelling booking',
        message:
          'This booking is no longer active. It may have already been cancelled, removed, or checked out.',
        type: AlertType.Error,
        closeable: true,
      };
    }

    // if reservation is currently checked in by the user
    if (reservation?.canCheckOut) {
      return <AlertInfo>{
        title: 'Error cancelling booking',
        message: 'You cannot cancel a booking after checking in. Please check out instead.',
        type: AlertType.Error,
        closeable: true,
      };
    }

    return null;
  }

  private getBookedByIcons(reservation: any): TableIcon[] {
    const isMyReservation = reservation?.user?.emailAddress == this.emailAddress;
    if (isMyReservation) {
      return [
        {
          iconName: 'person',
          tooltip: 'My reservation',
          ariaLabel: 'My reservation',
        },
      ];
    }

    const reservationEmail: string = reservation?.user?.emailAddress ?? '';
    return [
      {
        iconName: 'group',
        tooltip: `Reservation for ${reservationEmail}`,
        ariaLabel: reservationEmail,
      },
    ];
  }

  private getMyBookingActions(reservation: any): TableActionGroup {
    const canCheckOut = !!reservation?.canCheckOut;
    const canCheckIn =
      !!reservation?.canCheckIn &&
      (this.isSuperUser || reservation?.user?.emailAddress == reservation?.booker);

    if (canCheckOut) {
      return {
        button: {
          actionId: 'check-out',
          label: 'Check-out',
          ariaLabel: 'Check-out',
          cssClass: 'action-checkout-button',
          disabled: this.isBulkCancelling,
          variant: 'raised',
        },
      };
    }

    if (canCheckIn) {
      return {
        button: {
          actionId: 'cancel',
          label: 'Cancel',
          ariaLabel: 'Cancel booking',
          cssClass: 'action-cancel-button',
          disabled: this.isBulkCancelling,
          variant: 'stroked',
        },
        secondaryButton: {
          actionId: 'check-in',
          label: 'Check-in',
          ariaLabel: 'Check-in',
          cssClass: 'action-checkin-button',
          disabled: this.isBulkCancelling,
          variant: 'raised',
        },
      };
    }

    const hasAnyCheckInOrCheckOut: boolean = this.hasMyBookingsCheckInOrCheckOut();

    return {
      button: {
        actionId: 'cancel',
        label: 'Cancel',
        ariaLabel: 'Cancel booking',
        cssClass: 'action-cancel-button',
        disabled: this.isBulkCancelling,
        variant: 'stroked',
      },
      ...(hasAnyCheckInOrCheckOut && {
        secondaryButton: {
          actionId: 'placeholder',
          label: 'Placeholder',
          ariaLabel: 'Placeholder',
          cssClass: 'action-placeholder',
          disabled: true,
          variant: 'raised',
        },
      }),
    };
  }

  private getSearchBookingActions(reservation: any): TableActionGroup {
    const canCheckOut = !!reservation?.canCheckOut;
    const canCheckIn = !!reservation?.canCheckIn;

    if (canCheckOut) {
      return {
        button: {
          actionId: 'check-out',
          label: 'Check-out',
          ariaLabel: 'Check-out',
          cssClass: 'action-checkout-button',
          variant: 'raised',
        },
        secondaryButton: {
          actionId: 'resend-email',
          label: 'Send Email',
          ariaLabel: 'Send booking email',
          cssClass: 'search-send-email-button',
          variant: 'raised',
        },
      };
    }

    if (canCheckIn) {
      return {
        button: {
          actionId: 'cancel',
          label: 'Cancel',
          ariaLabel: 'Cancel booking',
          cssClass: 'action-cancel-button',
          variant: 'stroked',
        },
        secondaryButton: {
          actionId: 'check-in',
          label: 'Check-in',
          ariaLabel: 'Check-in',
          cssClass: 'action-checkin-button',
          variant: 'raised',
        },
        tertiaryButton: {
          actionId: 'resend-email',
          label: 'Send Email',
          ariaLabel: 'Send booking email',
          cssClass: 'search-send-email-button',
          variant: 'raised',
        },
      };
    }

    const hasAnyCheckInOrCheckOut: boolean = this.hasSearchBookingsCheckInOrCheckOut();

    return {
      button: {
        actionId: 'cancel',
        label: 'Cancel',
        ariaLabel: 'Cancel booking',
        cssClass: 'action-cancel-button',
        variant: 'stroked',
      },
      ...(hasAnyCheckInOrCheckOut && {
        secondaryButton: {
          actionId: 'placeholder',
          label: 'Placeholder',
          ariaLabel: 'Placeholder',
          cssClass: 'action-placeholder',
          disabled: true,
          variant: 'raised',
        },
      }),
      tertiaryButton: {
        actionId: 'resend-email',
        label: 'Send Email',
        ariaLabel: 'Send booking email',
        cssClass: 'search-send-email-button',
        variant: 'raised',
      },
    };
  }

  private buildReservationAlertMessage(action: string, reservation?: any): string {
    action = action.toLowerCase();

    const deskNumber: string = reservation?.workstation?.number ?? reservation?.workstationNumber;
    const date: string = this.formatDate(reservation?.date ?? new Date());
    const timePeriod: string = reservation?.reservationTimeSlot?.name;

    const message: string =
      deskNumber && timePeriod
        ? `Desk ${deskNumber} has been ${action} for ${date} (${timePeriod}).`
        : `Booking has been ${action} for ${date}.`;

    return message;
  }

  private hasMyBookingsCheckInOrCheckOut(): boolean {
    return this.myBookingsData.some((reservation) => {
      const canCheckOut: boolean = !!reservation?.canCheckOut;
      const canCheckIn: boolean =
        !!reservation?.canCheckIn &&
        (this.isSuperUser || reservation?.user?.emailAddress == reservation?.booker);
      return canCheckOut || canCheckIn;
    });
  }

  private hasSearchBookingsCheckInOrCheckOut(): boolean {
    return this.searchBookingsData.some((reservation) => {
      const canCheckOut: boolean = !!reservation?.canCheckOut;
      const canCheckIn: boolean = !!reservation?.canCheckIn;
      return canCheckOut || canCheckIn;
    });
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.onReservationSyncEvent);
    }

    this.destroy$.next();
    this.destroy$.complete();

    this.alertService.clearAlert();
  }
}
